import React from "react";
import { Link } from "react-router-dom";
import { Footer } from "../../components/Footer";
import { SEOHead } from "../../components/SEOHead";

/**
 * Dedicated Services page — detailed, marketing-focused overview of
 * everything Etlyn builds. Tailwind CSS only, no Mantine.
 */

/* ────────────────────────────────────────────────────────── */
/*  Data                                                      */
/* ────────────────────────────────────────────────────────── */

const services = [
  {
    id: "web",
    label: "Web Applications",
    headline: "Web apps that convert visitors into paying customers",
    description:
      "We design and engineer high-performance web applications that load fast, rank well and guide every visitor toward a conversion. Whether you need a customer portal, a marketplace, or a complex SaaS dashboard — we build it with your revenue goals at the centre.",
    capabilities: [
      "Single-page and multi-page application architecture",
      "Responsive design that works flawlessly on every device",
      "SEO-optimised rendering for maximum organic reach",
      "Real-time features — chat, notifications, live dashboards",
      "Third-party integrations (payment gateways, CRMs, analytics)",
      "Performance tuning — sub-second load times out of the box",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A8.966 8.966 0 0 1 3 12c0-1.264.26-2.467.732-3.559" />
      </svg>
    ),
    blob: "from-[#EEF1FB]/80",
    accent: "bg-[#EEF1FB]",
    accentText: "text-[#0D2B7E]",
  },
  {
    id: "mobile",
    label: "Mobile Applications",
    headline: "Put your business in your customers' pockets",
    description:
      "Native-quality mobile apps for iOS and Android that keep your audience engaged on the go. From push notifications and offline access to biometric login and in-app purchases — we build the features that drive retention and repeat revenue.",
    capabilities: [
      "Cross-platform development — one codebase, both stores",
      "Offline-first architecture for unreliable connections",
      "Push notifications and in-app messaging",
      "Biometric authentication and secure storage",
      "In-app purchases, subscriptions and payment processing",
      "App Store and Google Play submission and optimisation",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    blob: "from-[#F3EEFF]/80",
    accent: "bg-[#F3EEFF]",
    accentText: "text-[#6B21A8]",
  },
  {
    id: "cms",
    label: "CMS & Content Platforms",
    headline: "Publish content in minutes — no developers required",
    description:
      "Every project we build ships with an integrated content management system. Your marketing team can create, edit and publish blogs, landing pages, campaigns and product content without writing a single line of code — and without a separate CMS subscription.",
    capabilities: [
      "Visual editor with real-time content preview",
      "Blog management with categories, tags and scheduling",
      "Landing page builder for campaigns and promotions",
      "SEO metadata, sitemaps and open-graph tag management",
      "Multi-language support for international audiences",
      "Role-based access — editors, marketers and admins",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
    blob: "from-[#EFF9FF]/80",
    accent: "bg-[#EFF9FF]",
    accentText: "text-[#0369A1]",
  },
  {
    id: "ecommerce",
    label: "E-Commerce & SaaS",
    headline: "Platforms engineered to sell, subscribe and scale",
    description:
      "End-to-end transactional platforms — from product catalogues and checkout flows to subscription billing and analytics dashboards. We build the infrastructure that turns your idea into recurring revenue, whether you're selling products, services or software.",
    capabilities: [
      "Product catalogues with search, filters and variants",
      "Secure checkout with multiple payment providers",
      "Subscription and recurring billing management",
      "Customer dashboards and self-service portals",
      "Inventory tracking and order management",
      "Revenue analytics and conversion reporting",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
      </svg>
    ),
    blob: "from-[#EAFCEE]/80",
    accent: "bg-[#EAFCEE]",
    accentText: "text-[#166534]",
  },
  {
    id: "api",
    label: "API & Backend Systems",
    headline: "Rock-solid backends that power everything you build",
    description:
      "Modern, scalable APIs and backend services designed from the ground up to handle growth. We architect systems that stay fast at 10 users and at 100,000 — with built-in security, caching and monitoring so you can focus on your business, not your infrastructure.",
    capabilities: [
      "RESTful and real-time API design",
      "Authentication and role-based access control",
      "Database architecture and query optimisation",
      "Cloud deployment with auto-scaling",
      "Third-party service integration and webhooks",
      "Comprehensive API documentation and SDKs",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Zm-3 6h.008v.008h-.008v-.008Zm0-6h.008v.008h-.008v-.008Z" />
      </svg>
    ),
    blob: "from-[#FEF8E1]/80",
    accent: "bg-[#FEF8E1]",
    accentText: "text-[#92400E]",
  },
  {
    id: "consulting",
    label: "Strategy & Consulting",
    headline: "Technology strategy that aligns with your business goals",
    description:
      "Not sure where to start? We work with founders, CTOs and product managers to define the right technology roadmap — one that maximises ROI, reduces risk and gets your product to market faster. From technical audits to full product strategy, we've got you covered.",
    capabilities: [
      "Technical discovery and feasibility assessment",
      "Product roadmap and feature prioritisation",
      "Architecture review and performance audits",
      "Technology stack recommendations",
      "Team augmentation and developer mentoring",
      "Go-to-market technology planning",
    ],
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.5-1.5h9.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
    blob: "from-[#FDE8EC]/80",
    accent: "bg-[#FDE8EC]",
    accentText: "text-[#9F1239]",
  },
];

const industries = [
  {
    name: "Retail & E-Commerce",
    description: "Online stores, marketplaces and omni-channel platforms that drive sales.",
    icon: "🛍️",
  },
  {
    name: "Healthcare & Wellness",
    description: "Patient portals, booking systems and HIPAA-compliant platforms.",
    icon: "🏥",
  },
  {
    name: "Finance & Fintech",
    description: "Secure dashboards, payment systems and financial data platforms.",
    icon: "💳",
  },
  {
    name: "Education & E-Learning",
    description: "Course platforms, student portals and learning management systems.",
    icon: "🎓",
  },
  {
    name: "Real Estate & Property",
    description: "Listing platforms, virtual tours and property management tools.",
    icon: "🏠",
  },
  {
    name: "Startups & SaaS",
    description: "MVPs, SaaS platforms and scalable products for rapid growth.",
    icon: "🚀",
  },
];

const differentiators = [
  {
    title: "Revenue First, Code Second",
    description:
      "Every decision — from architecture to UI — is driven by your business metrics. We don't build features for the sake of it; we build features that move numbers.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
      </svg>
    ),
  },
  {
    title: "No Vendor Lock-In",
    description:
      "You own every line of code. No proprietary frameworks, no recurring platform fees, no surprises. Walk away with everything if you ever need to.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>
    ),
  },
  {
    title: "CMS Included — Free",
    description:
      "While others charge extra for content management, we build it in. Your marketing team publishes content from day one without waiting on developers.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
      </svg>
    ),
  },
  {
    title: "Transparent Pricing",
    description:
      "Fixed-scope or time-and-materials — you choose. Weekly progress updates, clear invoices, and no hidden costs. Your budget stays predictable from start to finish.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
      </svg>
    ),
  },
];

const process = [
  {
    step: "01",
    title: "Discovery Call",
    duration: "Week 1",
    description:
      "We learn your goals, audience and competitive landscape. You walk away with a clear scope, timeline and investment estimate — no strings attached.",
  },
  {
    step: "02",
    title: "Design & Prototype",
    duration: "Weeks 2 – 3",
    description:
      "Interactive wireframes and visual mockups that map every user journey. We test with real people and refine until the UX is conversion-ready.",
  },
  {
    step: "03",
    title: "Build & Iterate",
    duration: "Weeks 4 – 10",
    description:
      "Agile sprints with live demos every two weeks. Working software you can touch, test and give feedback on — so there are zero surprises at launch.",
  },
  {
    step: "04",
    title: "Launch & Scale",
    duration: "Ongoing",
    description:
      "We deploy to production, monitor performance and iterate based on real user data. Your product keeps getting better long after the first release.",
  },
];

/* ────────────────────────────────────────────────────────── */
/*  Component                                                 */
/* ────────────────────────────────────────────────────────── */

export default function Services() {
  const isMob = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 overflow-x-hidden mt-[80px] font-['Inter',sans-serif]">
      <SEOHead
        title="Our Services — Web, Mobile, CMS & E-Commerce Development"
        description="Explore Etlyn's full-stack development services: custom web applications, mobile apps, CMS platforms, e-commerce solutions and API integrations for growing businesses."
        path="/services"
        keywords="web development services, mobile app development, CMS development, e-commerce platform, SaaS development, API integration, custom software services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          provider: { "@type": "Organization", name: "Etlyn" },
          name: "Software Development Services",
          description: "Full-stack development services including web applications, mobile apps, CMS platforms, e-commerce and API integrations.",
          serviceType: ["Web Application Development", "Mobile App Development", "CMS Development", "E-Commerce Development"],
        }}
      />

      {/* ═══════════════════════════════════════════════════ */}
      {/*  HERO                                              */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="pt-8 pb-16 sm:pt-14 sm:pb-24 text-center">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-4">
          Our Services
        </span>
        <h1 className="text-2xl sm:text-3xl md:text-[2.75rem] md:leading-[1.15] font-bold text-[#333D51] tracking-tight mb-5 max-w-3xl mx-auto">
          Everything you need to launch, grow&nbsp;and&nbsp;dominate your&nbsp;market
        </h1>
        <p className="text-sm sm:text-base text-[#666D7C] leading-relaxed max-w-2xl mx-auto mb-10">
          From the first line of code to the last customer conversion — we
          build full-stack digital products that make your competitors
          nervous. Web, mobile, CMS, e-commerce and more, all under one roof.
        </p>

        {/* Quick nav pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {services.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#DFDFE0] bg-white px-4 py-2 text-xs sm:text-sm font-medium text-[#333D51] transition-all hover:border-[#0D2B7E] hover:text-[#0D2B7E] hover:shadow-sm"
            >
              {s.label}
            </a>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  DETAILED SERVICE SECTIONS                         */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="space-y-10 mb-24">
        {services.map((service, idx) => {
          const isEven = idx % 2 === 0;
          return (
            <div
              key={service.id}
              id={service.id}
              className="scroll-mt-28 relative overflow-hidden rounded-2xl bg-white ring-1 ring-[#DFDFE0]/60"
            >
              {/* Decorative blob */}
              <div
                className={`pointer-events-none absolute ${
                  isEven ? "-right-10 -top-10" : "-left-10 -bottom-10"
                } h-44 w-44 rounded-full bg-gradient-to-bl ${service.blob} to-transparent`}
              />

              <div className="relative p-6 sm:p-10">
                {/* Header row */}
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${service.accent} ${service.accentText}`}
                  >
                    {service.icon}
                  </div>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#808692] mb-1">
                      {service.label}
                    </span>
                    <h2 className="text-lg sm:text-xl font-bold text-[#333D51] tracking-tight leading-snug">
                      {service.headline}
                    </h2>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm sm:text-[15px] text-[#666D7C] leading-relaxed mb-8 max-w-3xl">
                  {service.description}
                </p>

                {/* Capabilities grid */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {service.capabilities.map((cap, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2.5 text-sm text-[#333D51] leading-relaxed"
                    >
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 text-[#28E454]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                      {cap}
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 pt-6 border-t border-[#DFDFE0]/60">
                  <Link
                    to="/home#contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0D2B7E] hover:underline"
                  >
                    Discuss your {service.label.toLowerCase()} project
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  WHY ETLYN — DIFFERENTIATORS                       */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="mb-24">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
            Why Etlyn
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight max-w-xl mx-auto">
            What makes us different from every other agency
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {differentiators.map((d) => (
            <div
              key={d.title}
              className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60 transition-shadow hover:shadow-md"
            >
              <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-bl from-[#EEF1FB]/80 to-transparent" />
              <div className="relative flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#0D2B7E] to-[#25408B] text-white">
                  {d.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#333D51] mb-1">
                    {d.title}
                  </h3>
                  <p className="text-sm text-[#666D7C] leading-relaxed">
                    {d.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  INDUSTRIES                                        */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="mb-24">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
            Industries We Serve
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight max-w-xl mx-auto">
            Proven experience across sectors that matter
          </h2>
          <p className="text-sm text-[#808692] mt-2 max-w-lg mx-auto">
            Every industry has unique challenges. We bring the domain
            understanding to solve them — and the engineering talent to
            ship on time.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {industries.map((ind) => (
            <div
              key={ind.name}
              className="rounded-2xl bg-white p-5 ring-1 ring-[#DFDFE0]/60 transition-shadow hover:shadow-md"
            >
              <span className="text-2xl mb-3 block">{ind.icon}</span>
              <h3 className="text-sm font-semibold text-[#333D51] mb-1">
                {ind.name}
              </h3>
              <p className="text-xs text-[#666D7C] leading-relaxed">
                {ind.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  PROCESS                                           */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="mb-24">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
            How We Work
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight max-w-xl mx-auto">
            A transparent process with no surprises
          </h2>
          <p className="text-sm text-[#808692] mt-2 max-w-lg mx-auto">
            Four straightforward steps from first conversation to live,
            revenue-generating product.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {process.map((p) => (
            <div
              key={p.step}
              className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60"
            >
              <span className="absolute -right-2 -top-4 text-[72px] font-extrabold leading-none text-[#EEF1FB] select-none">
                {p.step}
              </span>
              <div className="relative">
                <span className="inline-block rounded-full bg-[#EEF1FB] px-2.5 py-0.5 text-[10px] font-semibold text-[#0D2B7E] uppercase tracking-wider mb-3">
                  {p.duration}
                </span>
                <h3 className="text-base font-semibold text-[#333D51] mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-[#666D7C] leading-relaxed">
                  {p.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  SOCIAL PROOF STRIP                                */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="mb-24 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { metric: "50+", label: "Projects delivered across 6 industries" },
          { metric: "3×", label: "Faster time to market vs. in-house teams" },
          { metric: "99.9%", label: "Uptime across production deployments" },
          { metric: "40%", label: "Average lift in conversion after launch" },
        ].map((r) => (
          <div
            key={r.label}
            className="relative overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-[#DFDFE0]/60 text-center"
          >
            <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-bl from-[#EEF1FB]/80 to-transparent" />
            <div className="relative">
              <span className="block text-2xl sm:text-3xl font-bold text-[#0D2B7E] tracking-tight">
                {r.metric}
              </span>
              <span className="block mt-1 text-xs sm:text-sm text-[#666D7C] leading-snug">
                {r.label}
              </span>
            </div>
          </div>
        ))}
      </section>

      {/* ═══════════════════════════════════════════════════ */}
      {/*  CTA                                               */}
      {/* ═══════════════════════════════════════════════════ */}
      <section className="mb-16 rounded-2xl bg-gradient-to-br from-[#0D2B7E] to-[#25408B] px-6 py-14 sm:px-12 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
          Ready to build something&nbsp;great?
        </h2>
        <p className="text-sm sm:text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
          Tell us about your project — we'll come back with a plan, a
          timeline and a price. No obligation, no hard sell.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/home#contact"
            className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-[#0D2B7E] shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
            </svg>
            Start a Conversation
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 px-6 py-3 text-sm font-medium text-white transition hover:border-white/60"
          >
            Learn More About Us
          </Link>
        </div>
      </section>

      <Footer isMob={isMob} />
    </div>
  );
}
